import type { ComboboxOptionsProps } from '@headlessui/react';
import type { ReactNode } from 'react';
import type { DropdownOption } from '../DropdownOption/DropdownOption';

import { ComboboxOptions } from '@headlessui/react';
import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import DropdownOptionWrapper from '../DropdownOption/DropdownOption';

interface ComboboxOptionsWrapperProps extends ComboboxOptionsProps {
  additionalDropdownClasses?: string;
  testId?: string;
}

const ComboboxOptionsWrapper = ({
  children,
  additionalDropdownClasses,
  testId,
  ...props
}: ComboboxOptionsWrapperProps) => {
  return (
    <ComboboxOptions
      className={twMerge(
        'absolute top-full mt-2 max-h-40 w-full rounded-md overflow-auto overflow-overlay',
        'scrollbar-sm bg-white dark:bg-slate-900 dark:border dark:border-slate-800 text-base shadow-md',
        'focus:outline-hidden z-20 transition ease-in duration-100 data-closed:opacity-0 ',
        'py-px', // when virtual scrolling is set, without set padding/height the options are not visible
        additionalDropdownClasses,
      )}
      as="ul"
      modal={false}
      {...getTestProps(testId, 'options')}
      {...props}
    >
      {children}
    </ComboboxOptions>
  );
};

interface DropdownOptionsProps extends ComboboxOptionsProps {
  handleAddOnEmpty?: ReactNode;
  groupedOptions?: { [key: string]: DropdownOption[] };
  renderWithoutOptions?: ReactNode;
  isVirtualScrolling?: boolean;
  additionalDropdownClasses?: string;
  additionalOptionsClasses?: string;
  testId?: string;
}

const DropdownOptions = ({
  handleAddOnEmpty,
  groupedOptions,
  renderWithoutOptions,
  isVirtualScrolling,
  additionalDropdownClasses,
  additionalOptionsClasses,
  testId,
  ...props // headless ui passed props to combobox options
}: DropdownOptionsProps) => {
  if (isVirtualScrolling) {
    return (
      <ComboboxOptionsWrapper
        additionalDropdownClasses={additionalDropdownClasses}
        testId={testId}
        {...props}
      >
        {({ option }) => (
          <DropdownOptionWrapper
            option={option}
            isVirtualScrolling={isVirtualScrolling}
            additionalOptionsClasses={additionalOptionsClasses}
            testId={testId}
          />
        )}
      </ComboboxOptionsWrapper>
    );
  }

  return (
    <ComboboxOptionsWrapper
      additionalDropdownClasses={additionalDropdownClasses}
      testId={testId}
      {...props}
    >
      {renderWithoutOptions
        ? renderWithoutOptions
        : Object.keys(groupedOptions || {}).map((groupName) => (
            <Fragment key={groupName}>
              {groupName && (
                <li
                  className={twMerge(
                    'italic font-bold py-2 pl-4 pr-9 dark:text-white',
                  )}
                  key={groupName}
                  {...getTestProps(testId, `${groupName}-group`)}
                >
                  {groupName}
                </li>
              )}
              <>
                {handleAddOnEmpty}
                {groupedOptions &&
                  groupedOptions[groupName] &&
                  groupedOptions[groupName].map((option) => (
                    <DropdownOptionWrapper
                      key={option.value}
                      option={option}
                      additionalOptionsClasses={additionalOptionsClasses}
                      testId={testId}
                    />
                  ))}
              </>
            </Fragment>
          ))}
    </ComboboxOptionsWrapper>
  );
};

export default DropdownOptions;
